import { z } from 'zod';
export const WorkItemTypeSchema = z.enum([
    'epic',
    'feature',
    'task',
    'subtask',
    'bug',
    'story',
    'spike'
]);
export const WorkItemStatusSchema = z.enum([
    'open',
    'in_progress',
    'review',
    'testing',
    'blocked',
    'completed',
    'cancelled',
    'closed'
]);
export const WorkItemPrioritySchema = z.enum([
    'critical',
    'high',
    'medium',
    'low',
    'backlog'
]);
export const AssignmentStatusSchema = z.enum([
    'available',
    'assigned',
    'claimed',
    'active',
    'paused',
    'completed',
    'reassigned'
]);
export const ApprovalStatusSchema = z.enum([
    'pending',
    'approved',
    'rejected',
    'cancelled',
    'expired'
]);
export const WorkItemSchema = z.object({
    id: z.string().min(1),
    type: WorkItemTypeSchema,
    title: z.string().min(1).max(200),
    description: z.string().optional(),
    status: WorkItemStatusSchema,
    priority: WorkItemPrioritySchema,
    parent_id: z.string().optional(),
    children_ids: z.array(z.string()).default([]),
    dependencies: z.array(z.string()).default([]),
    blocks: z.array(z.string()).default([]),
    assignee: z.string().optional(),
    reporter: z.string().optional(),
    labels: z.array(z.string()).default([]),
    tags: z.array(z.string()).default([]),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    due_date: z.string().datetime().optional(),
    estimated_hours: z.number().positive().optional(),
    actual_hours: z.number().nonnegative().optional(),
    progress_percentage: z.number().min(0).max(100).default(0),
    story_points: z.number().positive().optional(),
    external_id: z.string().optional(),
    external_url: z.string().url().optional(),
    provider_metadata: z.record(z.any()).optional(),
    metadata: z.record(z.any()).optional(),
});
export const WorkItemQuerySchema = z.object({
    ids: z.array(z.string()).optional(),
    types: z.array(WorkItemTypeSchema).optional(),
    statuses: z.array(WorkItemStatusSchema).optional(),
    priorities: z.array(WorkItemPrioritySchema).optional(),
    assignees: z.array(z.string()).optional(),
    reporters: z.array(z.string()).optional(),
    unassigned: z.boolean().optional(),
    parent_id: z.string().optional(),
    has_children: z.boolean().optional(),
    depth_level: z.number().nonnegative().optional(),
    title_contains: z.string().optional(),
    description_contains: z.string().optional(),
    labels: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    created_after: z.string().datetime().optional(),
    created_before: z.string().datetime().optional(),
    updated_after: z.string().datetime().optional(),
    updated_before: z.string().datetime().optional(),
    due_after: z.string().datetime().optional(),
    due_before: z.string().datetime().optional(),
    progress_min: z.number().min(0).max(100).optional(),
    progress_max: z.number().min(0).max(100).optional(),
    limit: z.number().positive().max(1000).default(50),
    offset: z.number().nonnegative().default(0),
    sort_by: z.enum(['created_at', 'updated_at', 'priority', 'due_date', 'title']).default('updated_at'),
    sort_order: z.enum(['asc', 'desc']).default('desc'),
});
export const CreateWorkItemInputSchema = z.object({
    type: WorkItemTypeSchema,
    title: z.string().min(1).max(200),
    description: z.string().optional(),
    priority: WorkItemPrioritySchema.default('medium'),
    parent_id: z.string().optional(),
    dependencies: z.array(z.string()).default([]),
    assignee: z.string().optional(),
    labels: z.array(z.string()).default([]),
    tags: z.array(z.string()).default([]),
    due_date: z.string().datetime().optional(),
    estimated_hours: z.number().positive().optional(),
    story_points: z.number().positive().optional(),
    external_id: z.string().optional(),
    provider_metadata: z.record(z.any()).optional(),
    metadata: z.record(z.any()).optional(),
});
export const UpdateWorkItemInputSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().optional(),
    status: WorkItemStatusSchema.optional(),
    priority: WorkItemPrioritySchema.optional(),
    assignee: z.string().optional(),
    labels: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    due_date: z.string().datetime().optional(),
    estimated_hours: z.number().positive().optional(),
    actual_hours: z.number().nonnegative().optional(),
    story_points: z.number().positive().optional(),
    progress_percentage: z.number().min(0).max(100).optional(),
    parent_id: z.string().optional(),
    add_dependencies: z.array(z.string()).optional(),
    remove_dependencies: z.array(z.string()).optional(),
    add_blocks: z.array(z.string()).optional(),
    remove_blocks: z.array(z.string()).optional(),
    provider_metadata: z.record(z.any()).optional(),
    metadata: z.record(z.any()).optional(),
});
export const AgentAssignmentSchema = z.object({
    id: z.string().min(1),
    work_item_id: z.string().min(1),
    agent_id: z.string().min(1),
    agent_name: z.string().min(1),
    status: AssignmentStatusSchema,
    assigned_at: z.string().datetime(),
    claimed_at: z.string().datetime().optional(),
    started_at: z.string().datetime().optional(),
    completed_at: z.string().datetime().optional(),
    estimated_effort_hours: z.number().positive().optional(),
    actual_effort_hours: z.number().nonnegative().optional(),
    confidence_level: z.number().min(0).max(1).optional(),
    assignment_reason: z.string().optional(),
    skills_required: z.array(z.string()).default([]),
    prerequisites: z.array(z.string()).default([]),
    last_update: z.string().datetime().optional(),
    notes: z.string().optional(),
    metadata: z.record(z.any()).optional(),
});
export const ClaimWorkItemInputSchema = z.object({
    work_item_id: z.string().min(1),
    agent_id: z.string().min(1),
    agent_name: z.string().min(1),
    estimated_effort_hours: z.number().positive().optional(),
    confidence_level: z.number().min(0).max(1).optional(),
    notes: z.string().optional(),
    metadata: z.record(z.any()).optional(),
});
export const ProgressUpdateSchema = z.object({
    id: z.string().min(1),
    work_item_id: z.string().min(1),
    agent_id: z.string().min(1),
    status_change: z.object({
        from: WorkItemStatusSchema,
        to: WorkItemStatusSchema,
    }).optional(),
    progress_percentage: z.number().min(0).max(100).optional(),
    hours_logged: z.number().nonnegative().optional(),
    summary: z.string().min(1),
    description: z.string().optional(),
    timestamp: z.string().datetime(),
    artifacts: z.array(z.object({
        name: z.string().min(1),
        type: z.enum(['code', 'document', 'design', 'test', 'deployment', 'other']),
        path: z.string().optional(),
        url: z.string().url().optional(),
        description: z.string().optional(),
        size_bytes: z.number().nonnegative().optional(),
        checksum: z.string().optional(),
    })).default([]),
    blockers: z.array(z.object({
        id: z.string().min(1),
        description: z.string().min(1),
        severity: z.enum(['low', 'medium', 'high', 'critical']),
        created_at: z.string().datetime(),
        resolved_at: z.string().datetime().optional(),
    })).default([]),
    next_steps: z.array(z.string()).default([]),
    estimated_completion: z.string().datetime().optional(),
    metadata: z.record(z.any()).optional(),
});
export const CreateProgressUpdateInputSchema = z.object({
    work_item_id: z.string().min(1),
    agent_id: z.string().min(1),
    summary: z.string().min(1),
    description: z.string().optional(),
    progress_percentage: z.number().min(0).max(100).optional(),
    hours_logged: z.number().nonnegative().optional(),
    status_change: z.object({
        to: WorkItemStatusSchema,
    }).optional(),
    artifacts: z.array(z.object({
        name: z.string().min(1),
        type: z.enum(['code', 'document', 'design', 'test', 'deployment', 'other']),
        path: z.string().optional(),
        url: z.string().url().optional(),
        description: z.string().optional(),
    })).default([]),
    blockers: z.array(z.object({
        description: z.string().min(1),
        severity: z.enum(['low', 'medium', 'high', 'critical']),
    })).default([]),
    next_steps: z.array(z.string()).default([]),
    estimated_completion: z.string().datetime().optional(),
    metadata: z.record(z.any()).optional(),
});
export const ApprovalRequestSchema = z.object({
    id: z.string().min(1),
    work_item_id: z.string().min(1),
    requester_id: z.string().min(1),
    type: z.enum(['completion', 'status_change', 'assignment_change', 'scope_change']),
    status: ApprovalStatusSchema,
    priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
    title: z.string().min(1).max(200),
    description: z.string().optional(),
    justification: z.string().optional(),
    required_approvers: z.array(z.string()).min(1),
    current_approver: z.string().optional(),
    approvals: z.array(z.object({
        approver_id: z.string().min(1),
        status: z.enum(['pending', 'approved', 'rejected']),
        timestamp: z.string().datetime().optional(),
        comments: z.string().optional(),
    })).default([]),
    created_at: z.string().datetime(),
    due_date: z.string().datetime().optional(),
    resolved_at: z.string().datetime().optional(),
    proposed_changes: z.record(z.any()).optional(),
    artifacts: z.array(z.object({
        name: z.string().min(1),
        url: z.string().url().optional(),
        description: z.string().optional(),
    })).default([]),
    metadata: z.record(z.any()).optional(),
});
export const CreateApprovalRequestInputSchema = z.object({
    work_item_id: z.string().min(1),
    requester_id: z.string().min(1),
    type: z.enum(['completion', 'status_change', 'assignment_change', 'scope_change']),
    priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
    title: z.string().min(1).max(200),
    description: z.string().optional(),
    justification: z.string().optional(),
    required_approvers: z.array(z.string()).min(1),
    due_date: z.string().datetime().optional(),
    proposed_changes: z.record(z.any()).optional(),
    artifacts: z.array(z.object({
        name: z.string().min(1),
        url: z.string().url().optional(),
        description: z.string().optional(),
    })).default([]),
    metadata: z.record(z.any()).optional(),
});
export const ApprovalResponseInputSchema = z.object({
    approval_request_id: z.string().min(1),
    approver_id: z.string().min(1),
    decision: z.enum(['approved', 'rejected']),
    comments: z.string().optional(),
    metadata: z.record(z.any()).optional(),
});
export const WorkItemCommentSchema = z.object({
    id: z.string().min(1),
    work_item_id: z.string().min(1),
    author_id: z.string().min(1),
    author_name: z.string().min(1),
    content: z.string().min(1),
    content_type: z.enum(['text', 'markdown', 'html']).default('text'),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime().optional(),
    edited: z.boolean().default(false),
    parent_comment_id: z.string().optional(),
    reply_count: z.number().nonnegative().default(0),
    visibility: z.enum(['public', 'team', 'assignees', 'private']).default('public'),
    mentions: z.array(z.string()).default([]),
    attachments: z.array(z.object({
        name: z.string().min(1),
        url: z.string().url(),
        type: z.string(),
        size_bytes: z.number().nonnegative().optional(),
    })).default([]),
    external_id: z.string().optional(),
    external_url: z.string().url().optional(),
    metadata: z.record(z.any()).optional(),
});
export const CreateCommentInputSchema = z.object({
    work_item_id: z.string().min(1),
    author_id: z.string().min(1),
    author_name: z.string().min(1),
    content: z.string().min(1),
    content_type: z.enum(['text', 'markdown', 'html']).default('text'),
    parent_comment_id: z.string().optional(),
    visibility: z.enum(['public', 'team', 'assignees', 'private']).default('public'),
    mentions: z.array(z.string()).default([]),
    attachments: z.array(z.object({
        name: z.string().min(1),
        url: z.string().url(),
        type: z.string(),
    })).default([]),
    metadata: z.record(z.any()).optional(),
});
export class PMValidationError extends Error {
    field;
    value;
    code;
    constructor(message, field, value, code) {
        super(message);
        this.field = field;
        this.value = value;
        this.code = code;
        this.name = 'PMValidationError';
    }
}
export class PMOperationError extends Error {
    operation;
    entityType;
    entityId;
    context;
    constructor(message, operation, entityType, entityId, context) {
        super(message);
        this.operation = operation;
        this.entityType = entityType;
        this.entityId = entityId;
        this.context = context;
        this.name = 'PMOperationError';
    }
}
export class PMConflictError extends PMOperationError {
    conflictReason;
    constructor(message, operation, entityType, entityId, conflictReason) {
        super(message, operation, entityType, entityId);
        this.conflictReason = conflictReason;
        this.name = 'PMConflictError';
    }
}
export function validateWorkItemHierarchy(item, parentItem) {
    const errors = [];
    if (parentItem) {
        const typeHierarchy = {
            epic: ['feature', 'story'],
            feature: ['task', 'story', 'bug'],
            story: ['task', 'subtask'],
            task: ['subtask'],
            subtask: [],
            bug: ['subtask'],
            spike: ['task', 'subtask']
        };
        const allowedChildTypes = typeHierarchy[parentItem.type] || [];
        if (!allowedChildTypes.includes(item.type)) {
            errors.push(`${item.type} cannot be a child of ${parentItem.type}`);
        }
        if (item.id === parentItem.id) {
            errors.push('Work item cannot be its own parent');
        }
        if (parentItem.children_ids.includes(item.id)) {
            errors.push('Circular dependency detected in work item hierarchy');
        }
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
export function validateAgentAssignment(assignment, existingAssignments = []) {
    const errors = [];
    const activeAssignments = existingAssignments.filter(a => a.work_item_id === assignment.work_item_id &&
        ['assigned', 'claimed', 'active'].includes(a.status));
    if (activeAssignments.length > 0 && assignment.status !== 'reassigned') {
        errors.push('Work item is already assigned to another agent');
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
export function calculateHierarchicalProgress(workItem, childItems) {
    if (childItems.length === 0) {
        return workItem.progress_percentage;
    }
    const totalProgress = childItems.reduce((sum, child) => sum + child.progress_percentage, 0);
    return Math.round(totalProgress / childItems.length);
}
export function validateApprovalWorkflow(request, workItem) {
    const errors = [];
    if (request.type === 'completion' && workItem.status !== 'review') {
        errors.push('Work item must be in review status before completion approval');
    }
    if (request.required_approvers.length === 0) {
        errors.push('At least one approver is required');
    }
    const pendingApprovals = request.approvals.filter(a => a.status === 'pending');
    if (pendingApprovals.length === 0 && request.status === 'pending') {
        errors.push('No pending approvals found for pending approval request');
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
//# sourceMappingURL=pm-contracts.js.map